import type { FC, ReactNode } from 'react'

import {
    ArrowUp,
    ChevronLeft,
    CircleCheck,
    CircleDashed,
    LoaderCircle
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { LogoMark } from '@/components/generals/logo'
import { SkyPanel } from '@/components/generals/sky-panel'
import { Foliage } from '@/components/landing/foliage'

interface GrowthCardProps {
    title: string
    description: string
    delayClass: string
    children: ReactNode
}

const SCHEDULE_ROWS: Array<{ label: string, status: string, state: 'done' | 'working' | 'queued', delayClass: string }> = [
    { label: "Checked the inspector's calendar", status: 'Done', state: 'done', delayClass: 'reveal-d2' },
    { label: 'Buyers confirmed Oct 15, 10:00 am', status: 'Done', state: 'done', delayClass: 'reveal-d3' },
    { label: 'Listing agent notified', status: 'Sending', state: 'working', delayClass: 'reveal-d4' },
    { label: 'Lockbox code requested', status: 'Queued', state: 'queued', delayClass: 'reveal-d5' }
]

const GrowthCard: FC<GrowthCardProps> = ({ title, description, delayClass, children }): ReactNode => {
    return (
        <div className={cn('reveal', delayClass)}>
            <article className='group flex h-[25rem] flex-col overflow-hidden rounded-2xl border border-white/70 bg-paper/95 shadow-[0_24px_60px_-40px_rgba(38,56,92,0.6)] transition-[translate,box-shadow] duration-500 ease-out hover:-translate-y-1.5 hover:shadow-[0_40px_70px_-36px_rgba(38,56,92,0.7)] motion-reduce:hover:translate-y-0'>
                <div className='px-5 pt-5 sm:px-6 sm:pt-6'>
                    <h3 className='text-[17px] font-semibold text-foreground'>
                        {title}
                    </h3>

                    <p className='mt-2 text-[14px] leading-relaxed text-muted-foreground'>
                        {description}
                    </p>
                </div>

                <div aria-hidden='true' className='mt-auto px-5 transition-[translate] duration-500 ease-out group-hover:-translate-y-2 motion-reduce:group-hover:translate-y-0 sm:px-6'>
                    {children}
                </div>
            </article>
        </div>
    )
}

const ScheduleMini: FC = (): ReactNode => {
    return (
        <div className='rounded-t-xl border border-b-0 border-[#e9e6dd] bg-white p-3 shadow-[0_-10px_30px_-24px_rgba(35,34,30,0.35)]'>
            <p className='flex items-center gap-1.5 px-1 text-[11px] text-mist'>
                <LogoMark className='soft-pulse size-3.5' />
                Closewell is scheduling the inspection
            </p>

            <ul className='mt-2 space-y-1'>
                {SCHEDULE_ROWS.map(row => (
                    <li
                        key={row.label}
                        data-reveal='left'
                        className={cn(
                            'reveal flex items-center justify-between gap-2 rounded-lg px-2 py-2 text-[11.5px]',
                            row.delayClass,
                            row.state === 'working' ? 'bg-[#f3f1ea] text-foreground' : 'text-foreground/75'
                        )}
                    >
                        <span className='flex min-w-0 items-center gap-2'>
                            {row.state === 'done' && <CircleCheck className='size-3.5 shrink-0 text-leaf' />}
                            {row.state === 'working' && <LoaderCircle className='size-3.5 shrink-0 animate-spin text-honey motion-reduce:animate-none' />}
                            {row.state === 'queued' && <CircleDashed className='size-3.5 shrink-0 text-pebble' />}
                            <span className='truncate'>{row.label}</span>
                        </span>

                        <span className={cn('shrink-0 text-[10.5px]', row.state === 'working' ? 'soft-pulse text-honey' : 'text-mist')}>
                            {row.status}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    )
}

const ChatMini: FC = (): ReactNode => {
    return (
        <div className='rounded-t-xl border border-b-0 border-[#e9e6dd] bg-white p-3 shadow-[0_-10px_30px_-24px_rgba(35,34,30,0.35)]'>
            <p data-reveal='right' className='reveal reveal-d3 ml-auto max-w-[85%] rounded-2xl rounded-br-md bg-[#efece3] px-3 py-2 text-[11.5px] leading-snug text-foreground'>
                If the appraisal comes in low, can we still walk away?
            </p>

            <div data-reveal='left' className='reveal reveal-d5 mt-2.5 flex items-start gap-2'>
                <span className='flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground'>
                    <LogoMark className='size-3.5' />
                </span>

                <p className='text-[11.5px] leading-snug text-foreground/85'>
                    Yes. Your appraisal contingency runs through Oct 28, so you can renegotiate or cancel and keep your earnest money.
                </p>
            </div>

            <div className='mt-3 flex items-center justify-between rounded-lg border border-[#e9e6dd] py-1.5 pr-1.5 pl-3'>
                <span className='flex items-center gap-1 text-[11px] text-pebble'>
                    Ask about your deal
                    <span className='soft-pulse h-3 w-px bg-foreground/40' />
                </span>

                <span className='flex size-6 items-center justify-center rounded-md bg-primary text-primary-foreground'>
                    <ArrowUp className='size-3.5' />
                </span>
            </div>
        </div>
    )
}

const PhoneMini: FC = (): ReactNode => {
    return (
        <div className='mx-auto w-[11.5rem] rounded-t-[2rem] border-[5px] border-b-0 border-[#23221e] bg-white px-2.5 pt-2.5 shadow-[0_-10px_40px_-20px_rgba(35,34,30,0.45)]'>
            <div className='mx-auto h-4 w-16 rounded-full bg-[#23221e]' />

            <div className='mt-2 flex items-center justify-between border-b border-[#efede6] pb-2'>
                <ChevronLeft className='size-3.5 text-[#3478f6]' />

                <span className='flex flex-col items-center gap-0.5'>
                    <span className='flex size-6 items-center justify-center rounded-full bg-primary text-primary-foreground'>
                        <LogoMark className='size-3' />
                    </span>

                    <span className='text-[8.5px] text-mist'>Closewell</span>
                </span>

                <span className='w-3.5' />
            </div>

            <div className='space-y-1.5 py-2.5'>
                <p data-reveal='left' className='reveal reveal-d3 max-w-[88%] rounded-2xl rounded-bl-md bg-[#ecebe8] px-2.5 py-1.5 text-[9.5px] leading-snug text-foreground'>
                    Appraisal on 214 Wren Hollow came in at value. Share it with your buyers?
                </p>

                <p data-reveal='right' className='reveal reveal-d5 ml-auto w-fit rounded-2xl rounded-br-md bg-[#3478f6] px-2.5 py-1.5 text-[9.5px] leading-snug text-white'>
                    Yes, send it
                </p>

                <p data-reveal='left' className='reveal reveal-d6 max-w-[88%] rounded-2xl rounded-bl-md bg-[#ecebe8] px-2.5 py-1.5 text-[9.5px] leading-snug text-foreground'>
                    Sent. I will check in about loan approval on Nov 1.
                </p>
            </div>
        </div>
    )
}

export const GrowthSection: FC = (): ReactNode => {
    return (
        <section id='growth' aria-labelledby='growth-title' className='relative mt-28 sm:mt-36'>
            <Foliage side='left' parallax={0.14} className='-top-16 -left-[calc(min(30vw,460px)*0.05)] w-[min(30vw,460px)]' />

            <div className='px-2 sm:px-4 lg:px-5'>
                <SkyPanel mirror innerClassName='mx-auto max-w-6xl px-4 py-20 sm:px-8 sm:py-28'>
                    <div className='text-center'>
                        <h2 id='growth-title' className='reveal font-heading text-[clamp(3rem,6.4vw,5.25rem)] leading-[0.95] tracking-[-0.03em] text-foreground'>
                            Take on more deals,
                            <span className='block italic'>keep your weekends</span>
                        </h2>

                        <p className='reveal reveal-d1 mx-auto mt-6 max-w-[33rem] text-[17px] leading-[1.5] text-foreground/65 sm:text-lg'>
                            Closewell does not replace your coordinator. It gives every agent on your team room for a few more closings each month.
                        </p>
                    </div>

                    <div className='mt-14 grid gap-4 sm:mt-16 md:grid-cols-3 lg:gap-5'>
                        <GrowthCard
                            title='Inspections booked for you'
                            description='Closewell finds a time that works for the buyers, the inspector and the listing agent, then confirms it with everyone.'
                            delayClass='reveal-d1'
                        >
                            <ScheduleMini />
                        </GrowthCard>

                        <GrowthCard
                            title='Answers for clients at 10 pm'
                            description='Buyers ask questions in plain English and hear back from their own contract, not from your personal phone.'
                            delayClass='reveal-d2'
                        >
                            <ChatMini />
                        </GrowthCard>

                        <GrowthCard
                            title='Updates by text, not another inbox'
                            description='Agents and lenders get a quick text where they already reply, so the next step never waits on an unread email.'
                            delayClass='reveal-d3'
                        >
                            <PhoneMini />
                        </GrowthCard>
                    </div>
                </SkyPanel>
            </div>
        </section>
    )
}
